<template>
  <TravelersFilterPopup
    :title="t('title.header')"
    filter-key="tourModal"
    :is-disabled-reset-button="isDisabledResetButton"
    :modal-size="modalSize"
    modal-class="modal-filters"
    show-close-button
    is-fullscreen
    is-modal-only
    :total-count="categoryStore.experiencesTmpCount"
    :is-loading="categoryStore.isExperiencesCountLoading"
    @open="onPopupOpen"
    @close="onPopupClose"
    @apply="onApplyFilter"
    @reset="onResetFilter"
  >
    <template #trigger="{ open }">
      <div class="filters-open-btn">
        <SkeletonBlock
          :is-active="categoryStore.isSkeletonActive"
          :width="filterButtonSkeletonWidth"
          :height="48"
        >
          <UiButton
            v-if="!categoryStore.isSkeletonActive"
            :text="t('title.header')"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :size="currentSize"
            @click="open"
          >
            <template #icon><ModalFilterBtnIcon /></template>
          </UiButton>
          <div
            v-if="!categoryStore.isSkeletonActive && categoryStore.appliedFiltersCount"
            class="filters-count"
          >
            {{ categoryStore.appliedFiltersCount }}
          </div>
        </SkeletonBlock>
      </div>
    </template>

    <div class="modal-filters-list">
      <filter-row
        :title="t('title.priceTour', { currencySymbol: getCurrencySymbol(categoryStore.currencyCode) })"
        class="price-row"
      >
        <FilterPriceContent :is-modal="true" />
      </filter-row>

      <filter-row>
        <PersonStepper bold-title />
      </filter-row>

      <filter-row
        :title="t('title.duration')"
        class="duration-row"
      >
        <FilterDurationContent />
      </filter-row>

      <filter-row :title="t('title.comfort_type')">
        <ComfortCheckboxList />
      </filter-row>

      <filter-row :title="t('title.accommodation_type')">
        <AccommodationCheckboxList />
      </filter-row>

      <filter-row :title="t('title.activity_level')">
        <ActivityLevelCheckBoxList />
      </filter-row>

      <filter-row
        :title="t('title.children')"
        class="children-row"
      >
        <ChildAllowedCheckbox />
      </filter-row>

      <filter-row
        :title="t('title.sort')"
        class="sorting-row"
      >
        <RadioSortingList
          :selected-value="selectedSortingValue"
          @choose-sorting-value="onChooseSortingValue"
        />
      </filter-row>
    </div>
  </TravelersFilterPopup>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { TravelersFilterPopup } from '@experience/travelers-filter-popup-package';
  import { useScreenSize } from '@experience/shared';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import { useCategoryStore } from '@/shared/store';
  import { UiButton, UiButtonVariant, UiButtonColorMode, UiButtonSize } from '@experience/core-button-ui';
  import { ModalFilterBtnIcon } from '@/shared/ui/icon';
  import FilterRow from './FilterRow.vue';
  import { FilterPriceContent } from '@/features/filter/price';
  import { FilterDurationContent } from '@/features/filter/duration';
  import { ComfortCheckboxList } from '@/features/filter/comfort';
  import { AccommodationCheckboxList } from '@/features/filter/accommodation';
  import { ActivityLevelCheckBoxList } from '@/features/filter/activity-level';
  import { ChildAllowedCheckbox } from '@/features/filter/child-allowed';
  import { getCurrencySymbol } from '@/shared/lib/currency';
  import { PersonStepper } from '@/features/filter/dates';
  import { applyModalStyles, resetModalStyles } from '@/shared/lib';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { EExperienceFilterType } from '../model';
  import { RadioSortingList } from '@/features/sorting';
  import type { ListSortItemValue } from '@/shared/models';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const props = defineProps<{
    isScroll?: boolean;
  }>();

  const { isMobile } = useScreenSize();

  const modalSize = computed(() => (isMobile.value ? UiModalSize.FULLSCREEN : UiModalSize.S));
  const filterButtonSkeletonWidth = computed(() => (isMobile.value ? '100%' : 135));
  const currentSize = computed(() => {
    if (isMobile.value) {
      return UiButtonSize.M;
    }

    return props.isScroll ? UiButtonSize.S : UiButtonSize.M;
  });

  const isDisabledResetButton = computed(() => categoryStore.appliedTmpFiltersCount === 0);

  const categoryStore = useCategoryStore();
  /**
   * [method] открытие модального окна
   */
  const onPopupOpen = async () => {
    applyModalStyles();
  };
  /**
   * [method] закрытие модального окна
   */
  const onPopupClose = () => {
    resetModalStyles();

    onResetFilter();
  };
  /**
   * [method] применение фильтров
   */
  const onApplyFilter = async () => {
    /**
     * применение сортировки
     */
    await categoryStore.setSorting(selectedSortingValue.value);
    await categoryStore.onApplyFilter(
      Object.values(EExperienceFilterType).filter((type) => type !== EExperienceFilterType.SEO_DEFAULT),
    );
  };
  /**
   * [method] сброс фильтров
   */
  const onResetFilter = () => {
    categoryStore.onResetFilter(
      Object.values(EExperienceFilterType).filter((type) => type !== EExperienceFilterType.SEO_DEFAULT),
    );
  };
  /**
   * [method] выбор сортировки
   */
  const selectedSortingValue = ref<ListSortItemValue>(categoryStore.currentSorting);
  const onChooseSortingValue = (value: ListSortItemValue) => {
    selectedSortingValue.value = value;
  };

  watch(
    () => categoryStore.currentSorting,
    () => {
      /**
       * если значение сортировки в сторе поменялось - меняем тут
       */
      selectedSortingValue.value = categoryStore.currentSorting;
    },
  );
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .modal-filters-list {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .price-row,
    .duration-row,
    .children-row {
      gap: 16px;
    }

    .footer-persons {
      margin-bottom: 0;
    }
  }

  .movement-types {
    min-width: 516px;
  }

  .filters-open-btn {
    position: relative;
    display: flex;
    align-items: center;

    @include adaptivity.mobile {
      width: 100%;
    }
  }

  .filters-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 2px solid var(--comp-badge-color-border-default);
    border-radius: var(--alias-radius-rounded);
    background: var(--comp-badge-color-background-neutral);
    color: var(--comp-badge-color-text);
    font-weight: var(--global-typography-weight-regular);
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
  }

  .sorting-row {
    :deep(.sorting-element) {
      .radio {
        padding: 12px 0;
      }
    }
  }
</style>
